import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getPatients } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { BookOpen, Loader2, UserRound } from "lucide-react";
import { toast } from "sonner";

function coercePatientsArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  if (Array.isArray(payload.patients)) return payload.patients;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

function computeAgeFromBirthDate(fechaNacimiento) {
  if (!fechaNacimiento) return null;
  const born = new Date(fechaNacimiento);
  if (Number.isNaN(born.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - born.getFullYear();
  const monthDiff = today.getMonth() - born.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < born.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : null;
}

function patientDisplayAge(patient) {
  if (patient?.edad != null && patient.edad !== "" && patient.edad !== "—") {
    return patient.edad;
  }
  return computeAgeFromBirthDate(patient?.fecha_nacimiento);
}

function patientFullName(patient) {
  return [patient?.nombres, patient?.apellidos].filter(Boolean).join(" ").trim() || "Paciente sin nombre";
}

function sortByCasoId(a, b) {
  return (a?.caso_id || "").localeCompare(b?.caso_id || "", undefined, { numeric: true });
}

function hasCasoId(patient) {
  return Boolean((patient?.caso_id ?? "").trim());
}

export default function AulaCasos() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCasos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPatients();
      const list = coercePatientsArray(res?.data)
        .filter(hasCasoId)
        .slice()
        .sort(sortByCasoId);
      setPatients(list);
    } catch {
      toast.error("No se pudieron cargar los casos clínicos");
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCasos();
  }, [fetchCasos]);

  return (
    <div className="mx-auto max-w-5xl space-y-8 animate-fade-in">
      <header className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
          Casos clínicos
        </h1>
        <p className="max-w-2xl text-base text-slate-600">
          Estudia estos casos con VGI completa resuelta
        </p>
      </header>

      {loading ? (
        <div className="flex min-h-[240px] flex-col items-center justify-center gap-3 text-violet-700">
          <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
          <p className="text-sm font-medium">Cargando casos clínicos...</p>
        </div>
      ) : patients.length === 0 ? (
        <Card className="border-violet-200/80 bg-white">
          <CardContent className="flex min-h-[240px] flex-col items-center justify-center gap-3 px-6 py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-violet-100 bg-violet-50 text-violet-600">
              <BookOpen className="h-6 w-6" strokeWidth={1.75} />
            </div>
            <div className="space-y-1">
              <p className="text-base font-semibold text-slate-900">No hay casos disponibles</p>
              <p className="text-sm text-slate-600">
                Los casos precargados aún no están configurados para tu cuenta académica.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {patients.map((patient) => {
            const age = patientDisplayAge(patient);
            const name = patientFullName(patient);
            return (
              <Card
                key={patient.id}
                className="flex flex-col border-violet-200/80 bg-white shadow-sm transition-shadow hover:shadow-md"
              >
                <CardHeader className="pb-3">
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-violet-100 bg-violet-50 text-violet-700">
                      <UserRound className="h-5 w-5" strokeWidth={1.75} />
                    </div>
                    {patient.caso_id && (
                      <span className="rounded-full border border-violet-200 bg-violet-50 px-2.5 py-0.5 text-xs font-medium text-violet-800">
                        {patient.caso_id.replace("_", " ")}
                      </span>
                    )}
                  </div>
                  <CardTitle className="text-lg leading-snug text-slate-900">{name}</CardTitle>
                  <p className="text-sm text-slate-500">
                    {age != null ? `${age} años` : "Edad no disponible"}
                  </p>
                </CardHeader>
                <CardContent className="flex flex-1 flex-col gap-4 pt-0">
                  <p className="flex-1 text-sm leading-relaxed text-slate-600">
                    {patient.diagnostico_principal || "Sin diagnóstico principal registrado"}
                  </p>
                  <Button
                    asChild
                    className="w-full rounded-xl bg-violet-600 text-white hover:bg-violet-700"
                  >
                    <Link to={`/aula/casos/${patient.id}`}>Ver caso</Link>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
